import { io } from 'socket.io-client';
import {
  SOCKET_URL,
  SOCKET_EVENTS,
  SOCKET_NAMESPACES,
} from '../config/constants';

/**
 * Service de gestion des connexions Socket.io par namespace
 */
class SocketService {
  sockets = {
    stream: null,
    chat: null,
    sounds: null,
    followed: null,
    rewards: null,
  };

  /**
   * Méthode générique pour se connecter à un namespace
   * @param {string} namespace - Namespace à utiliser
   * @param {string} url - URL du serveur Socket.io
   * @returns {Object} Instance Socket.io du namespace
   */
  connectToNamespace(namespace, url = SOCKET_URL) {
    const key = this.getNamespaceKey(namespace);
    if (!this.sockets[key]) {
      // Socket.io client s'attend à recevoir l'URL de base du serveur
      // Le namespace est spécifié dans l'URL complète
      // Si url est vide ou commence par /, on utilise window.location.origin
      let baseUrl;
      if (!url || url === '/') {
        // Si pas d'URL fournie, utiliser l'origine actuelle (frontend)
        // Mais en production avec Traefik, on devrait toujours avoir une URL configurée
        console.warn(
          '⚠️ SOCKET_URL non défini, utilisation de window.location.origin'
        );
        baseUrl = window.location.origin;
      } else if (url.startsWith('http://') || url.startsWith('https://')) {
        baseUrl = url;
      } else if (url.startsWith('ws://') || url.startsWith('wss://')) {
        // Convertir ws:// en http:// et wss:// en https:// pour Socket.IO
        baseUrl = url.replace(/^ws:/, 'http:').replace(/^wss:/, 'https:');
      } else if (url.startsWith('/')) {
        // URL relative, utiliser l'origine actuelle
        baseUrl = window.location.origin;
      } else {
        // URL sans protocole, ajouter le protocole actuel
        baseUrl = `${window.location.protocol}//${url}`;
      }
      // Construire l'URL complète avec le namespace
      // Socket.io client ajoutera automatiquement /socket.io/ au début
      const namespaceUrl = `${baseUrl}${namespace}`;
      console.log(
        `🔌 Connexion Socket.IO à: ${namespaceUrl} (namespace: ${namespace})`
      );
      // En production avec certificat auto-signé, polling est plus fiable que websocket
      // car les navigateurs peuvent bloquer wss:// avec un certificat invalide
      this.sockets[key] = io(namespaceUrl, {
        path: '/socket.io',
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        withCredentials: true,
      });

      this.sockets[key].on(SOCKET_EVENTS.CONNECT, () => {
        console.log(`✅ Connecté au namespace ${namespace}`);
      });

      this.sockets[key].on(SOCKET_EVENTS.DISCONNECT, (reason) => {
        console.log(`❌ Déconnecté du namespace ${namespace}:`, reason);
      });

      this.sockets[key].on('connect_error', (error) => {
        console.error(
          `❌ Erreur de connexion au namespace ${namespace}:`,
          error.message
        );
      });

      this.sockets[key].on('reconnect', (attemptNumber) => {
        console.log(
          `🔄 Reconnexion réussie au namespace ${namespace} après ${attemptNumber} tentative(s)`
        );
      });

      this.sockets[key].on('reconnect_attempt', () => {
        console.log(`🔄 Tentative de reconnexion au namespace ${namespace}...`);
      });

      this.sockets[key].on('reconnect_failed', () => {
        console.error(
          `❌ Échec de la reconnexion au namespace ${namespace} après toutes les tentatives`
        );
      });
    }
    return this.sockets[key];
  }

  /**
   * Obtient la clé du namespace à partir de son chemin
   * @param {string} namespace - Chemin du namespace (ex: '/stream')
   * @returns {string} Clé du namespace (ex: 'stream')
   */
  getNamespaceKey(namespace) {
    const mapping = {
      [SOCKET_NAMESPACES.STREAM]: 'stream',
      [SOCKET_NAMESPACES.CHAT]: 'chat',
      [SOCKET_NAMESPACES.SOUNDS]: 'sounds',
      [SOCKET_NAMESPACES.FOLLOWED]: 'followed',
      [SOCKET_NAMESPACES.REWARDS]: 'rewards',
      [SOCKET_NAMESPACES.OVERLAYS]: 'overlays',
    };
    return mapping[namespace] || namespace.replace('/', '');
  }

  /**
   * Connecte au namespace WS-STREAM
   */
  connectToStream(url = SOCKET_URL) {
    return this.connectToNamespace(SOCKET_NAMESPACES.STREAM, url);
  }

  /**
   * Connecte au namespace WS-CHAT
   */
  connectToChat(url = SOCKET_URL) {
    return this.connectToNamespace(SOCKET_NAMESPACES.CHAT, url);
  }

  /**
   * Connecte au namespace WS-SOUNDS (public, sans authentification)
   * Utilisé par le SoundPlayer pour OBS
   */
  connectToSounds(url = SOCKET_URL) {
    return this.connectToNamespacePublic(SOCKET_NAMESPACES.SOUNDS, url);
  }

  /**
   * Connecte au namespace WS-OVERLAYS (public, sans authentification)
   * Utilisé par le OverlayPlayer pour OBS
   */
  connectToOverlays(url = SOCKET_URL) {
    return this.connectToNamespacePublic(SOCKET_NAMESPACES.OVERLAYS, url);
  }

  /**
   * Méthode pour se connecter à un namespace public (sans authentification)
   * @param {string} namespace - Namespace à utiliser
   * @param {string} url - URL du serveur Socket.io
   * @returns {Object} Instance Socket.io du namespace
   */
  connectToNamespacePublic(namespace, url = SOCKET_URL) {
    const key = this.getNamespaceKey(namespace);
    if (!this.sockets[key]) {
      let baseUrl;
      if (!url || url === '/') {
        console.warn(
          '⚠️ SOCKET_URL non défini, utilisation de window.location.origin'
        );
        baseUrl = window.location.origin;
      } else if (url.startsWith('http://') || url.startsWith('https://')) {
        baseUrl = url;
      } else if (url.startsWith('ws://') || url.startsWith('wss://')) {
        baseUrl = url.replace(/^ws:/, 'http:').replace(/^wss:/, 'https:');
      } else if (url.startsWith('/')) {
        baseUrl = window.location.origin;
      } else {
        baseUrl = `${window.location.protocol}//${url}`;
      }
      const namespaceUrl = `${baseUrl}${namespace}`;
      console.log(
        `🔌 Connexion Socket.IO publique à: ${namespaceUrl} (namespace: ${namespace})`
      );

      // Connexion SANS authentification pour les namespaces publics
      // En production avec certificat auto-signé, polling est plus fiable que websocket
      // car les navigateurs peuvent bloquer wss:// avec un certificat invalide
      this.sockets[key] = io(namespaceUrl, {
        path: '/socket.io',
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        // Pas de token d'authentification
      });

      this.sockets[key].on(SOCKET_EVENTS.CONNECT, () => {
        console.log(`✅ Connecté au namespace public ${namespace}`);
      });

      this.sockets[key].on(SOCKET_EVENTS.DISCONNECT, (reason) => {
        console.log(`❌ Déconnecté du namespace public ${namespace}:`, reason);
      });

      this.sockets[key].on('connect_error', (error) => {
        console.error(
          `❌ Erreur de connexion au namespace public ${namespace}:`,
          error.message
        );
      });

      this.sockets[key].on('reconnect', (attemptNumber) => {
        console.log(
          `🔄 Reconnexion réussie au namespace public ${namespace} après ${attemptNumber} tentative(s)`
        );
      });

      this.sockets[key].on('reconnect_attempt', () => {
        console.log(
          `🔄 Tentative de reconnexion au namespace public ${namespace}...`
        );
      });

      this.sockets[key].on('reconnect_failed', () => {
        console.error(
          `❌ Échec de la reconnexion au namespace public ${namespace} après toutes les tentatives`
        );
      });
    }
    return this.sockets[key];
  }

  /**
   * Connecte au namespace WS-FOLLOWED
   */
  connectToFollowed(url = SOCKET_URL) {
    return this.connectToNamespace(SOCKET_NAMESPACES.FOLLOWED, url);
  }

  /**
   * Connecte au namespace WS-REWARDS
   */
  connectToRewards(url = SOCKET_URL) {
    return this.connectToNamespace(SOCKET_NAMESPACES.REWARDS, url);
  }

  /**
   * Connecte au serveur Socket.io (namespace par défaut - DÉPRÉCIÉ)
   * @deprecated Utilisez connectToStream, connectToChat, connectToFollowed ou connectToRewards
   */
  connect(url = SOCKET_URL) {
    console.warn(
      '⚠️ SocketService.connect() est déprécié. Utilisez connectToStream(), connectToChat(), connectToFollowed() ou connectToRewards()'
    );
    // Pour compatibilité, on retourne le socket chat par défaut
    return this.connectToChat(url);
  }

  /**
   * Déconnecte d'un namespace spécifique
   * @param {string} namespace - Namespace à déconnecter
   */
  disconnectFromNamespace(namespace) {
    const key = this.getNamespaceKey(namespace);
    if (this.sockets[key]) {
      this.sockets[key].disconnect();
      this.sockets[key] = null;
      console.log(`🔌 Déconnecté du namespace ${namespace}`);
    }
  }

  /**
   * Déconnecte tous les namespaces
   */
  disconnect() {
    Object.keys(this.sockets).forEach((key) => {
      if (this.sockets[key]) {
        this.sockets[key].disconnect();
        this.sockets[key] = null;
      }
    });
    console.log('🔌 Tous les namespaces Socket.io déconnectés');
  }

  /**
   * Écoute un événement sur un namespace spécifique
   * @param {string} namespace - Namespace à utiliser
   * @param {string} event - Nom de l'événement
   * @param {Function} callback - Fonction de callback
   */
  onNamespace(namespace, event, callback) {
    const key = this.getNamespaceKey(namespace);
    if (this.sockets[key]) {
      this.sockets[key].on(event, callback);
    }
  }

  /**
   * Arrête d'écouter un événement sur un namespace spécifique
   * @param {string} namespace - Namespace à utiliser
   * @param {string} event - Nom de l'événement
   * @param {Function} callback - Fonction de callback
   */
  offNamespace(namespace, event, callback) {
    const key = this.getNamespaceKey(namespace);
    if (this.sockets[key]) {
      this.sockets[key].off(event, callback);
    }
  }

  /**
   * Émet un événement sur un namespace spécifique
   * @param {string} namespace - Namespace à utiliser
   * @param {string} event - Nom de l'événement
   * @param {*} data - Données à émettre
   */
  emitNamespace(namespace, event, data) {
    const key = this.getNamespaceKey(namespace);
    if (this.sockets[key]) {
      this.sockets[key].emit(event, data);
    }
  }

  /**
   * Écoute un événement (compatibilité - utilise le namespace chat par défaut)
   * @deprecated Utilisez onNamespace() avec le namespace approprié
   */
  on(event, callback) {
    console.warn(
      '⚠️ SocketService.on() est déprécié. Utilisez onNamespace() avec le namespace approprié'
    );
    // Pour compatibilité, on utilise le namespace chat par défaut
    this.onNamespace(SOCKET_NAMESPACES.CHAT, event, callback);
  }

  /**
   * Arrête d'écouter un événement (compatibilité - utilise le namespace chat par défaut)
   * @deprecated Utilisez offNamespace() avec le namespace approprié
   */
  off(event, callback) {
    console.warn(
      '⚠️ SocketService.off() est déprécié. Utilisez offNamespace() avec le namespace approprié'
    );
    // Pour compatibilité, on utilise le namespace chat par défaut
    this.offNamespace(SOCKET_NAMESPACES.CHAT, event, callback);
  }

  /**
   * Émet un événement (compatibilité - utilise le namespace chat par défaut)
   * @deprecated Utilisez emitNamespace() avec le namespace approprié
   */
  emit(event, data) {
    console.warn(
      '⚠️ SocketService.emit() est déprécié. Utilisez emitNamespace() avec le namespace approprié'
    );
    // Pour compatibilité, on utilise le namespace chat par défaut
    this.emitNamespace(SOCKET_NAMESPACES.CHAT, event, data);
  }

  /**
   * Vérifie si la connexion est active (compatibilité - vérifie le namespace chat)
   * @deprecated Utilisez isNamespaceConnected() avec le namespace approprié
   */
  isConnected() {
    const key = this.getNamespaceKey(SOCKET_NAMESPACES.CHAT);
    return this.sockets[key] && this.sockets[key].connected;
  }

  /**
   * Vérifie si un namespace est connecté
   * @param {string} namespace - Namespace à vérifier
   * @returns {boolean} True si connecté
   */
  isNamespaceConnected(namespace) {
    const key = this.getNamespaceKey(namespace);
    return this.sockets[key] && this.sockets[key].connected;
  }
}

const socketService = new SocketService();
export default socketService;
